import type { PluginListenerHandle } from '@capacitor/core';
import { StripeTerminal, TerminalEventsEnum } from '@am3nd-org/pointcraft-stripe-terminal';

import { ApiMethods, apiSenderV2, apiEndpoints } from 'src/utils/axios';

import { instrumentStripeTerminalClient } from './stripe-terminal-log';

let listenerPromise: Promise<PluginListenerHandle> | null = null;
const loggedStripeTerminal = instrumentStripeTerminalClient(StripeTerminal, { scope: 'token' });

export type StripeTerminalTokenScopeIdentity = {
  businessId: string;
  providerAccountId: string;
  businessDeviceId?: string;
  managerUserId?: string;
  installationId?: string;
  providerLocationId?: string;
  terminalId?: string;
  readerSerialNumber?: string;
};

type StripeTerminalTokenScope = StripeTerminalTokenScopeIdentity & {
  scopeKey: string;
  isCurrent: () => boolean;
  allowNetworkFetch: boolean;
};

type TokenRequestScope = {
  generation: number;
  identity: StripeTerminalTokenScopeIdentity | null;
  scopeKey: string;
  isCurrent: (() => boolean) | null;
  allowNetworkFetch: boolean;
};

export type StripeTerminalConnectionTokenPrime = StripeTerminalTokenScopeIdentity & {
  token: string;
  /** Prepared server flows provide an expiry; immediately consumed local-create tokens do not. */
  expiresAt?: string;
  isScopeCurrent: () => boolean;
};

type PrimedToken = {
  token: string;
  scopeKey: string;
  expiresAtMs: number | null;
};

type TokenEndpointResponse = {
  secret?: unknown;
  locationId?: unknown;
  providerAccountId?: unknown;
  message?: unknown;
  errorCode?: unknown;
};

let activeScope: StripeTerminalTokenScope | null = null;
let primedToken: PrimedToken | null = null;
let tokenScopeGeneration = 0;
let tokenDeliverySequence = 0;
const tokenRequestsInFlight = new Map<string, Promise<string>>();

function normalized(value: string | undefined): string {
  return value?.trim() ?? '';
}

export function stripeTerminalTokenScopeKey(identity: StripeTerminalTokenScopeIdentity): string {
  return [
    normalized(identity.businessId),
    identity.managerUserId ? `manager:${normalized(identity.managerUserId)}:${normalized(identity.installationId)}` : normalized(identity.businessDeviceId),
    normalized(identity.providerAccountId),
    normalized(identity.providerLocationId),
    normalized(identity.terminalId),
    normalized(identity.readerSerialNumber).toUpperCase(),
  ].join('|');
}

function normalizeIdentity(
  identity: StripeTerminalTokenScopeIdentity
): StripeTerminalTokenScopeIdentity {
  return {
    businessId: normalized(identity.businessId),
    ...(identity.managerUserId ? { managerUserId: normalized(identity.managerUserId), installationId: normalized(identity.installationId) } : {}),
    providerAccountId: normalized(identity.providerAccountId),
    ...(identity.businessDeviceId !== undefined
      ? { businessDeviceId: normalized(identity.businessDeviceId) }
      : {}),
    ...(identity.providerLocationId !== undefined
      ? { providerLocationId: normalized(identity.providerLocationId) }
      : {}),
    ...(identity.terminalId !== undefined ? { terminalId: normalized(identity.terminalId) } : {}),
    ...(identity.readerSerialNumber !== undefined
      ? { readerSerialNumber: normalized(identity.readerSerialNumber).toUpperCase() }
      : {}),
  };
}

function assertIdentity(identity: StripeTerminalTokenScopeIdentity): void {
  if (!identity.businessId || !identity.providerAccountId) {
    throw new Error('Stripe Terminal connection-token scope is invalid');
  }
}

function captureRequestScope(): TokenRequestScope {
  return activeScope
    ? {
        generation: tokenScopeGeneration,
        identity: activeScope,
        scopeKey: activeScope.scopeKey,
        isCurrent: activeScope.isCurrent,
        allowNetworkFetch: activeScope.allowNetworkFetch,
      }
    : {
        generation: tokenScopeGeneration,
        identity: null,
        scopeKey: 'unscoped',
        isCurrent: null,
        allowNetworkFetch: true,
      };
}

function requestScopeKey(scope: TokenRequestScope): string {
  return `${scope.generation}:${scope.scopeKey}`;
}

function isRequestScopeCurrent(scope: TokenRequestScope): boolean {
  if (scope.generation !== tokenScopeGeneration) return false;
  if (scope.identity === null) return activeScope === null;
  return (
    activeScope !== null &&
    activeScope.scopeKey === scope.scopeKey &&
    scope.isCurrent?.() === true &&
    activeScope.isCurrent()
  );
}

function consumePrimedToken(scope: TokenRequestScope): string | null {
  const prime = primedToken;
  if (!prime) return null;

  const isExpired = prime.expiresAtMs !== null && Date.now() >= prime.expiresAtMs;
  primedToken = null;
  return !isExpired && prime.scopeKey === scope.scopeKey ? prime.token : null;
}

function validateEndpointScope(
  response: TokenEndpointResponse,
  identity: StripeTerminalTokenScopeIdentity | null
): void {
  if (!identity) return;
  if (
    typeof response.providerAccountId === 'string' &&
    response.providerAccountId.trim() !== identity.providerAccountId
  ) {
    throw new Error('The connection token belongs to a different provider account.');
  }
  if (
    identity.providerLocationId &&
    typeof response.locationId === 'string' &&
    response.locationId.trim() !== identity.providerLocationId
  ) {
    throw new Error('The connection token belongs to a different reader location.');
  }
}

async function fetchConnectionToken(scope: TokenRequestScope): Promise<string> {
  if (!scope.allowNetworkFetch) {
    throw new Error('Stripe Terminal network token refresh is disabled for this scope');
  }
  const key = requestScopeKey(scope);
  const existingRequest = tokenRequestsInFlight.get(key);
  if (existingRequest) return existingRequest;

  const request = (async () => {
    const response = (await apiSenderV2(
      apiEndpoints.v4.stripeConnectionToken,
      ApiMethods.GET,
      {},
      true,
      null,
      { sensitiveResponse: true }
    )) as TokenEndpointResponse | null;

    validateEndpointScope(response ?? {}, scope.identity);
    if (typeof response?.secret === 'string' && response.secret.trim()) {
      return response.secret;
    }

    const message = typeof response?.message === 'string' ? response.message : null;
    const errorCode = typeof response?.errorCode === 'string' ? response.errorCode : null;
    throw new Error(message || errorCode || 'The endpoint returned no connection token.');
  })();
  tokenRequestsInFlight.set(key, request);

  try {
    return await request;
  } finally {
    if (tokenRequestsInFlight.get(key) === request) tokenRequestsInFlight.delete(key);
  }
}

export function invalidateStripeTerminalConnectionTokenScope(): void {
  tokenScopeGeneration += 1;
  primedToken = null;
  activeScope = null;
}

/** Forget a one-use credential without destroying the active reader identity. */
export function discardPrimedStripeTerminalConnectionToken(): void {
  primedToken = null;
}

export function ensureStripeTerminalConnectionTokenProvider(): Promise<PluginListenerHandle> {
  listenerPromise ??= loggedStripeTerminal.addListener(
    TerminalEventsEnum.RequestedConnectionToken,
    async () => {
      try {
        const requestedScope = captureRequestScope();
        if (!isRequestScopeCurrent(requestedScope)) {
          throw new Error('Stripe Terminal payment scope is no longer current');
        }

        const primed = consumePrimedToken(requestedScope);
        if (!primed && !requestedScope.allowNetworkFetch) {
          await loggedStripeTerminal.setConnectionToken({ token: '' }).catch(() => undefined);
          return;
        }

        const token = primed ?? (await fetchConnectionToken(requestedScope));
        if (!isRequestScopeCurrent(requestedScope)) {
          throw new Error('Stripe Terminal payment scope changed while refreshing credentials');
        }
        await loggedStripeTerminal.setConnectionToken({ token });
        tokenDeliverySequence += 1;
      } catch {
        console.error('[Stripe Terminal] Connection-token request failed');
      }
    }
  ).catch((error) => {
    listenerPromise = null;
    throw error;
  });

  return listenerPromise;
}

export async function primeStripeTerminalConnectionToken(
  input?: StripeTerminalConnectionTokenPrime
): Promise<void> {
  invalidateStripeTerminalConnectionTokenScope();
  await ensureStripeTerminalConnectionTokenProvider();

  if (!input) {
    const requestedScope = captureRequestScope();
    const token = await fetchConnectionToken(requestedScope);
    if (!isRequestScopeCurrent(requestedScope)) {
      throw new Error('Stripe Terminal discovery scope changed while refreshing credentials');
    }
    primedToken = { token, scopeKey: 'unscoped', expiresAtMs: null };
    return;
  }

  const identity = normalizeIdentity(input);
  assertIdentity(identity);
  const token = input.token.trim();
  const expiresAtMs = input.expiresAt === undefined ? null : Date.parse(input.expiresAt);
  if (!token || (expiresAtMs !== null && !Number.isFinite(expiresAtMs))) {
    throw new Error('Stripe Terminal connection-token scope is invalid');
  }

  activeScope = {
    ...identity,
    scopeKey: stripeTerminalTokenScopeKey(identity),
    isCurrent: input.isScopeCurrent,
    allowNetworkFetch: true,
  };
  if (!activeScope.isCurrent()) {
    activeScope = null;
    throw new Error('Stripe Terminal payment scope is no longer current');
  }
  primedToken = { token, scopeKey: activeScope.scopeKey, expiresAtMs };
}

export async function activateStripeTerminalOfflineConnectionTokenScope(
  input: StripeTerminalTokenScopeIdentity & { isScopeCurrent: () => boolean }
): Promise<void> {
  return activateStripeTerminalConnectionTokenScope({ ...input, allowNetworkFetch: false });
}

export async function activateStripeTerminalConnectionTokenScope(
  input: StripeTerminalTokenScopeIdentity & {
    isScopeCurrent: () => boolean;
    allowNetworkFetch: boolean;
  }
): Promise<void> {
  const identity = normalizeIdentity(input);
  assertIdentity(identity);
  const scopeKey = stripeTerminalTokenScopeKey(identity);
  if (activeScope?.scopeKey === scopeKey && activeScope.isCurrent() && input.isScopeCurrent()) {
    activeScope = {
      ...identity,
      scopeKey,
      isCurrent: input.isScopeCurrent,
      allowNetworkFetch: input.allowNetworkFetch,
    };
    if (!input.allowNetworkFetch) primedToken = null;
    await ensureStripeTerminalConnectionTokenProvider();
    return;
  }

  invalidateStripeTerminalConnectionTokenScope();
  await ensureStripeTerminalConnectionTokenProvider();
  activeScope = {
    ...identity,
    scopeKey,
    isCurrent: input.isScopeCurrent,
    allowNetworkFetch: input.allowNetworkFetch,
  };
  if (!activeScope.isCurrent()) {
    activeScope = null;
    throw new Error('Stripe Terminal payment scope is no longer current');
  }
}

/** Fetch and prime exactly one token for a full reader scope. Concurrent callers join. */
export async function fetchAndPrimeStripeTerminalConnectionToken(input: {
  identity: StripeTerminalTokenScopeIdentity;
  isScopeCurrent: () => boolean;
}): Promise<void> {
  await activateStripeTerminalConnectionTokenScope({
    ...input.identity,
    isScopeCurrent: input.isScopeCurrent,
    allowNetworkFetch: true,
  });
  const requestedScope = captureRequestScope();
  const deliverySequence = tokenDeliverySequence;
  const token = await fetchConnectionToken(requestedScope);
  if (!isRequestScopeCurrent(requestedScope)) {
    throw new Error('Stripe Terminal reader scope changed while refreshing credentials');
  }
  // The SDK callback may have joined this same request and already consumed
  // the single-use token. In that case there is nothing left to prime.
  if (tokenDeliverySequence === deliverySequence) {
    primedToken = { token, scopeKey: requestedScope.scopeKey, expiresAtMs: null };
  }
}

/** Legacy behavior used by admin/debug flows: clear the credential and its scope. */
export function clearPrimedStripeTerminalConnectionToken(): void {
  invalidateStripeTerminalConnectionTokenScope();
}
